import { navigate } from 'gatsby'

import type { Locale } from '@application/Types/Locale'
import { languages } from '@src/Application/Constants/languageConstants'
import { getAlternativePageUrl, onChangeLanguage } from '@src/Application/Interfaces/Organisms/LanguageMenu/utils'
import { AlternateUrlType, HeaderThemeModeType } from '@src/Application/Types/ComponentTypes'
import { HeaderNav } from '@src/Application/Types/HeaderTypes'
import { Nullable } from '@src/Application/Types/Opt'
import { sortingArray } from '@src/Application/Utils/generalUtils'

export type PendingNavigation = {
    lang: string
    alterPageUrl: string | null
} | null

type ToggleMenu = (setIsExpanded: (isExpanded: boolean) => void, value: boolean) => () => void
export const toggleMenu: ToggleMenu = (setIsExpanded, value) => () => {
    setIsExpanded(value)
}

type GetNatItem = (nodes: HeaderNav[], value: string) => HeaderNav[]
export const getNavNode: GetNatItem = (nodes, value) =>
    nodes.filter((node) => node.position === value).sort(sortingArray((data) => data.order))

type GetLastNavNode = (nodes: HeaderNav[]) => Nullable<HeaderNav>
export const getLastNavNode: GetLastNavNode = (node) => (node.length > 0 ? node[node.length - 1] : null)

type HandleChangeLanguage = ({
    setPendingNavigation,
    setIsExpanded,
    lang,
    pageUrl,
}: {
    setPendingNavigation: (pendingNavigation: PendingNavigation) => void
    setIsExpanded: (isExpanded: boolean) => void
    lang: string
    pageUrl: string | null
}) => () => void

export const handleChangeLanguage: HandleChangeLanguage = (params) => (): void => {
    const { setPendingNavigation, setIsExpanded, lang, pageUrl } = params

    setPendingNavigation({ lang: lang, alterPageUrl: pageUrl })

    setIsExpanded(false)
}

type GetMobileNav = ({
    leftItems,
    rightItems,
    language,
    languageLabel,
    alternateUrls,
    setPendingNavigation,
    setIsExpanded,
}: {
    leftItems: HeaderNav[]
    rightItems: HeaderNav[]
    language: Locale
    languageLabel: string
    alternateUrls?: Nullable<AlternateUrlType[]>
    organizationLanguages: Locale[]
    setPendingNavigation: (alterPageUrl: PendingNavigation) => void
    setIsExpanded: (isExpanded: boolean) => void
}) => HeaderNav[]

export const getMobileNav: GetMobileNav = ({
    leftItems,
    rightItems,
    language,
    languageLabel,
    alternateUrls,
    organizationLanguages,
    setPendingNavigation,
    setIsExpanded,
}) => {
    const totalItem = leftItems.length + rightItems.length

    const lngs = languages.filter((lang) => organizationLanguages.includes(lang.locale))

    if (lngs.length <= 1) {
        return [...leftItems, ...rightItems]
    }

    const pageUrl = getAlternativePageUrl(language, alternateUrls)

    const languageMenuItem: HeaderNav = {
        language,
        link: '',
        label: languageLabel,
        submenu: [
            {
                id: `${totalItem + 1}`,
                strapi_component: 'dynamic-columns',
                menuLinkSections: [
                    {
                        buttonLabel: null,
                        buttonStyle: null,
                        buttonUrl: null,
                        title: null,
                        link: lngs.map((item) => ({
                            name: item.name,
                            onClick: handleChangeLanguage({
                                setPendingNavigation,
                                setIsExpanded,
                                lang: item.locale,
                                pageUrl,
                            }),
                        })),
                        publishedAt: null,
                    },
                ],
            },
        ],
        position: 'right',
        order: totalItem,
    }
    return [...leftItems, languageMenuItem, ...rightItems]
}

export const getColorBasedMode = (mode: HeaderThemeModeType): string => (mode === 'Light' ? 'black' : 'white')

type HandleTransitionExited = ({
    pendingNavigation,
    setPendingNavigation,
}: {
    pendingNavigation: PendingNavigation
    setPendingNavigation: (pendingLang: PendingNavigation) => void
}) => () => void
export const handleTransitionExited: HandleTransitionExited = (params) => (): void => {
    const { pendingNavigation, setPendingNavigation } = params
    if (pendingNavigation) {
        onChangeLanguage(pendingNavigation.lang, navigate, pendingNavigation.alterPageUrl)()

        // Reset pending state
        setPendingNavigation(null)
    }
}
